import { DEFAULT_PAGE_SIZE } from "@/constants/miscConstants";
import { useCallback, useEffect, useState, SetStateAction, Dispatch } from "react";
import { useSearchParams } from "react-router-dom";

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const PAGE_PARAM = "page";

/**
 * Pagination hook that persists pageIndex in the URL and pageSize in localStorage.
 * Navigating back/forward in the browser restores the previously viewed page.
 *
 * @param storageKey - Key used to store pageSize preference in localStorage
 * @param defaultPageSize - Fallback page size if nothing is stored
 */
const useUrlPagination = (
  storageKey: string,
  defaultPageSize: number = DEFAULT_PAGE_SIZE,
): [PaginationState, Dispatch<SetStateAction<PaginationState>>] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getPageIndexFromUrl = useCallback(() => {
    const param = searchParams.get(PAGE_PARAM);
    const parsed = Number(param);
    return param != null && !isNaN(parsed) && parsed >= 1 ? parsed - 1 : 0;
  }, [searchParams]);

  const getPageSizeFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem("pagination");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed[storageKey]) return parsed[storageKey] as number;
      }
    } catch {
      /* ignore */
    }
    return defaultPageSize;
  }, [storageKey, defaultPageSize]);

  const [pagination, setPaginationInternal] = useState<PaginationState>(() => ({
    pageIndex: getPageIndexFromUrl(),
    pageSize: getPageSizeFromStorage(),
  }));

  // Keep state in sync when the URL changes (browser back/forward)
  useEffect(() => {
    const urlPageIndex = getPageIndexFromUrl();
    setPaginationInternal((prev) =>
      prev.pageIndex !== urlPageIndex ? { ...prev, pageIndex: urlPageIndex } : prev,
    );
  }, [getPageIndexFromUrl]);

  // Push pageIndex changes to the URL
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const newPage = pagination.pageIndex === 0 ? null : String(pagination.pageIndex + 1);
        const currentPage = prev.get(PAGE_PARAM);

        if (currentPage === newPage) return prev;

        if (newPage == null) {
          prev.delete(PAGE_PARAM);
        } else {
          prev.set(PAGE_PARAM, newPage);
        }
        return prev;
      },
      { replace: true },
    );
  }, [pagination.pageIndex, setSearchParams]);

  // Persist pageSize to localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("pagination");
      const parsed = stored ? JSON.parse(stored) : {};
      parsed[storageKey] = pagination.pageSize;
      localStorage.setItem("pagination", JSON.stringify(parsed));
    } catch {
      /* ignore */
    }
  }, [pagination.pageSize, storageKey]);

  const setPagination: Dispatch<SetStateAction<PaginationState>> = useCallback(
    (action) => setPaginationInternal(action),
    [],
  );

  return [pagination, setPagination];
};

export default useUrlPagination;
